import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui';
import { adminPermissionsQuery } from '@/queries';
import type { PermissionCell } from '@/lib/stock-types';

/**
 * Permissions. Spec 003 §9, FR-803 to FR-812.
 *
 * The client asked for this in their own words: the admin decides what each
 * role can view, create, edit and delete in each module, **without waiting for
 * a release**. This is that screen.
 *
 * Two things it is careful about.
 *
 * **The admin row is shown and disabled, not hidden.** Somebody looking for
 * "why can the admin do this" should find the answer rather than a gap, and the
 * database refuses the change regardless of what this screen renders.
 *
 * **A change takes effect on the next request.** There is no cache to clear and
 * no deploy, because every route asks the database the same question rather
 * than holding a copy of the answer.
 */

const ACTIONS = [
  ['can_view', 'View'],
  ['can_create', 'Create'],
  ['can_edit', 'Edit'],
  ['can_delete', 'Delete'],
] as const;

type ActionKey = (typeof ACTIONS)[number][0];

export default function AdminPermissionsPage() {
  const qc = useQueryClient();
  const { roles, modules, permissions, can_edit } = useSuspenseQuery(adminPermissionsQuery).data;
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const cell = new Map<string, PermissionCell>();
  for (const p of permissions) cell.set(`${p.role_key}:${p.module_key}`, p);

  const change = useMutation({
    mutationFn: async (c: { role_key: string; module_key: string } & Partial<Record<ActionKey, boolean>>) => {
      const res = await fetch('/api/permissions/matrix', {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ changes: [c] }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null);
      setSaved('Saved. It applies to the next thing anybody does: there is nothing to deploy.');
      void qc.invalidateQueries({ queryKey: ['admin', 'permissions'] });
    },
    onError: (e: Error) => {
      setSaved(null);
      setError(e.message);
    },
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Permissions</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          Set what each kind of account can do in each part of the system. A
          change applies immediately.
        </p>
      </div>

      {!can_edit && (
        <p className="mb-5 rounded-[12px] border border-line bg-panel p-4 text-[14px] text-muted">
          You can see this page and not change it. It is here because a person is
          allowed to know what they may do.
        </p>
      )}

      {error && (
        <p className="mb-4 rounded-[10px] border border-line bg-panel p-3 text-[14px]">{error}</p>
      )}
      {saved && (
        <p className="mb-4 rounded-[10px] border border-line bg-panel p-3 text-[14px] text-muted">
          {saved}
        </p>
      )}

      {roles.map((role) => (
        <section key={role.key} className="mb-8">
          <h2 className="mb-2 flex items-center gap-3 text-[16px] font-bold">
            {role.name}
            <Badge tone={role.kind === 'internal' ? 'brand' : 'ok'}>
              {role.kind === 'internal' ? 'works here' : 'customer'}
            </Badge>
            {role.locked && (
              <span className="text-[13px] font-normal text-muted">
                cannot be changed, by design
              </span>
            )}
          </h2>

          <div className="overflow-x-auto rounded-[10px] border border-line bg-panel">
            <table className="dt w-full min-w-[620px]">
              <thead>
                <tr className="border-b border-line text-left text-[12px] uppercase tracking-wide text-muted">
                <th>Part of the system</th>
                  {ACTIONS.map(([, label]) => (
                <th key={label} className="px-3 py-2 text-center">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((mod) => {
                  const c = cell.get(`${role.key}:${mod.key}`);
                  return (
                    <tr key={mod.key} className="border-b border-line last:border-0">
                <td>
                        <span className="font-medium">{mod.name}</span>
                        {mod.description && (
                          <span className="block text-[12px] text-muted">{mod.description}</span>
                        )}
                      </td>
                      {ACTIONS.map(([key]) => (
                <td key={key} className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            aria-label={`${role.name} may ${key.replace('can_', '')} ${mod.name}`}
                            checked={Boolean(c?.[key])}
                            disabled={role.locked || !can_edit || change.isPending}
                            onChange={(e) =>
                              change.mutate({
                                role_key: role.key,
                                module_key: mod.key,
                                [key]: e.target.checked,
                              })
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
